import {Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {PagamentoFaturaService} from "../payment-invoice/payment-invoice.service";
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";
import {lastValueFrom} from "rxjs";
import {MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef} from "@angular/material/dialog";
import {InvoiceNew} from "../models/InvoiceNew";
import {InvoiceService} from "../invoice/invoice.service";
import {MyProfileService} from '../my-profile/my-profile.service';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { A11yModule } from '@angular/cdk/a11y';
import { NgxMaskDirective } from 'ngx-mask';
import { aplicarCobranca, CamposNacionalidade, EscolhaCobranca, LocalEndereco, Nacionalidade, PAIS_PADRAO } from '../billing-nationality/billing-nationality';
import { PAISES_EXTERIOR } from '../billing-nationality/countries';

@Component({
  selector: 'app-modal-credit-card',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatCardModule, MatButtonModule, MatDialogModule, MatFormFieldModule, MatInputModule, MatCheckboxModule, MatRadioModule, MatSelectModule, MatIconModule, MatSlideToggleModule, MatProgressSpinnerModule, A11yModule, NgxMaskDirective],
  templateUrl: './modal-credit-card.component.html',
  styleUrls: ['./modal-credit-card.component.css']
})
export class ModalCreditCardComponent implements OnInit, OnDestroy {

  public form: FormGroup = new FormGroup({
    usarCartaoCadastrado: new FormControl(false, [Validators.required]),
    nacionalidade: new FormControl<Nacionalidade>('brasileiro'),
    localEndereco: new FormControl<LocalEndereco>('brasil'),
    nomeCartao: new FormControl('', [Validators.required]),
    numeroCartao: new FormControl('', [Validators.required]),
    vencimento: new FormControl('', [Validators.required]),
    cvv: new FormControl('', [Validators.required, Validators.minLength(3), Validators.maxLength(4)]),
    documento: new FormControl('', [Validators.required]),
    cep: new FormControl('', [Validators.required]),
    logradouro: new FormControl('', [Validators.required]),
    numero: new FormControl('', [Validators.required]),
    complemento: new FormControl('', []),
    bairro: new FormControl('', [Validators.required]),
    cidade: new FormControl('', [Validators.required]),
    estado: new FormControl('', [Validators.required]),
    pais: new FormControl(PAIS_PADRAO),
    celular: new FormControl('', [Validators.required]),
    email: new FormControl('', [Validators.required, Validators.email]),
    cadastrarRecorrencia: new FormControl(false, []),
  });
  public readonly paises = PAISES_EXTERIOR;
  private readonly camposNacionalidade: CamposNacionalidade = {
    documento: 'documento', pais: 'pais', cep: 'cep', estado: 'estado',
    telefone: 'celular', bairro: 'bairro', numero: 'numero',
  };
  private escolhaAtual: EscolhaCobranca = { nacionalidade: 'brasileiro', localEndereco: 'brasil' };
  public isSubmitting = false;
  public isFetchingCep = false;
  public submitError = '';

  public constructor(
    private faturaService: PagamentoFaturaService,
    private invoiceService: InvoiceService,
    private myProfileService: MyProfileService,
    @Inject(MAT_DIALOG_DATA) protected fatura: InvoiceNew,
    private matDialogRef: MatDialogRef<ModalCreditCardComponent>,
  ) {
  }

  async ngOnInit(): Promise<any> {
    const aplicar = () => {
      const nova: EscolhaCobranca = {
        nacionalidade: this.form.get('nacionalidade')?.value ?? 'brasileiro',
        localEndereco: this.form.get('localEndereco')?.value ?? 'brasil',
      };
      aplicarCobranca(this.form, nova, this.camposNacionalidade, this.escolhaAtual);
      this.escolhaAtual = nova;
    };
    this.form.get('nacionalidade')?.valueChanges.subscribe(aplicar);
    this.form.get('localEndereco')?.valueChanges.subscribe(aplicar);
    try {
      this.faturaService.showSpinner();
      const profile = await lastValueFrom(this.myProfileService.getMyProfile());
      this.form.patchValue({
        nomeCartao: profile.NOME,
        documento: profile.CODIGO_TIPO_PESSOA === 'F' ? profile.CPF : profile.CNPJ,
        cep: profile.CEP,
        logradouro: profile.LOGRADOURO,
        numero: profile.NUMERO,
        complemento: profile.COMPLEMENTO,
        bairro: profile.BAIRRO,
        cidade: profile.CIDADE,
        estado: profile.UF,
        celular: profile.TEL_CELULAR,
        email: profile.EMAIL,
      });
    } catch (error) {
      console.error(error);
    } finally {
      this.faturaService.hideSpinner();
    }
  }

  ngOnDestroy(): void {
    this.clearPaymentForm();
  }


  /** Nacionalidade: decide somente o CPF/CNPJ. */
  public get estrangeiro(): boolean {
    return this.form.get('nacionalidade')?.value === 'estrangeiro';
  }

  /** Localização do endereço de cobrança: decide o formato do endereço e o campo País. */
  public get exterior(): boolean {
    return this.form.get('localEndereco')?.value === 'exterior';
  }

  public async preencherCep(): Promise<any> {
    if (this.exterior) {
      return;
    }
    const cep = this.onlyDigits(this.form.get('cep')?.value);
    if (cep.length !== 8) {
      return;
    }

    try {
      this.isFetchingCep = true;
      this.faturaService.showSpinner();
      const dadosCep = await lastValueFrom(this.faturaService.cep(cep));
      this.form.patchValue({
        cep: dadosCep.cep,
        logradouro: dadosCep.logradouro,
        bairro: dadosCep.bairro,
        cidade: dadosCep.localidade,
        estado: dadosCep.uf,
      });
    } catch (e) {
      this.faturaService.toasterError('Não foi possível buscar o CEP. Confira os dados e preencha o endereço manualmente.');
    } finally {
      this.isFetchingCep = false;
      this.faturaService.hideSpinner();
    }
  }

  public async pagar(): Promise<any> {
    this.submitError = '';
    if (this.showCardFields && this.form.invalid) {
      this.form.markAllAsTouched();
      this.focusFirstInvalidField();
      this.submitError = 'Confira os campos obrigatórios antes de continuar.';
      return;
    }

    let success = false;
    try {
      this.isSubmitting = true;
      this.faturaService.showSpinner();
      const { nacionalidade, localEndereco, ...dados } = this.form.getRawValue();
      await lastValueFrom(this.invoiceService.payInvoiceWithCreditCard(this.fatura.CODIGO_FATURA.toString(), dados));
      success = true;
      this.faturaService.toasterSuccess("Aguarde alguns instantes. Estamos realizando o processamento das suas informações.", "Pagamento em processamento");
      this.matDialogRef.close();
    } catch (e: any) {
      this.form.get('cvv')?.reset();
      this.submitError = 'Não foi possível processar o pagamento. Confira os dados e tente novamente.';
      this.faturaService.toasterError(e?.error ?? this.submitError);
    } finally {
      this.isSubmitting = false;
      if (success) {
        this.clearPaymentForm();
      }
      this.faturaService.hideSpinner();
    }
  }

  getLogo(bandeira: string): string{
    return '/assets/images/cards/' + bandeira.toLowerCase() + '.svg';
  }

  public get cardBrand(): string {
    const number = this.onlyDigits(this.form.get('numeroCartao')?.value);

    if (/^4/.test(number)) {
      return 'Visa';
    }
    if (/^(5[1-5]|2[2-7])/.test(number)) {
      return 'Mastercard';
    }
    if (/^3[47]/.test(number)) {
      return 'Amex';
    }
    if (/^3(?:0[0-5]|[68])/.test(number)) {
      return 'Diners';
    }
    if (/^(4011|4312|4389|4514|4576|5041|5067|509|6277|6362|6363|650|6516|6550)/.test(number)) {
      return 'Elo';
    }
    if (/^35/.test(number)) {
      return 'JCB';
    }

    return '';
  }

  public get cardBrandLabel(): string {
    return this.cardBrand || '';
  }

  public get cardBrandLogo(): string {
    return this.cardBrand ? this.getLogo(this.cardBrand) : '';
  }

  public get cardPreviewNumber(): string {
    const number = this.onlyDigits(this.form.get('numeroCartao')?.value).slice(0, 16);
    const padded = number.padEnd(16, '•');
    return padded.match(/.{1,4}/g)?.join(' ') ?? '•••• •••• •••• ••••';
  }

  public get cardPreviewName(): string {
    return (this.form.get('nomeCartao')?.value || 'NOME IMPRESSO NO CARTÃO').trim().toUpperCase();
  }

  public get cardPreviewExpiry(): string {
    const expiry = this.onlyDigits(this.form.get('vencimento')?.value).slice(0, 6);
    if (!expiry) {
      return 'MM/AAAA';
    }

    const month = expiry.slice(0, 2).padEnd(2, 'M');
    const year = expiry.slice(2, 6).padEnd(4, 'A');
    return `${month}/${year}`;
  }

  public get paymentValueLabel(): string {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(this.fatura.VALOR_TOTAL ?? 0);
  }

  public get useRegisteredCard(): boolean {
    return !!this.form.get('usarCartaoCadastrado')?.value && !!this.fatura.CARTAO_CADASTRADO;
  }

  public get showCardFields(): boolean {
    return !this.useRegisteredCard;
  }

  public get canSubmit(): boolean {
    return !this.isSubmitting && (this.useRegisteredCard || this.form.valid);
  }

  public fieldInvalid(fieldName: string): boolean {
    const control = this.form.get(fieldName);
    return !!control && control.invalid && (control.touched || control.dirty);
  }

  public errorMessage(fieldName: string): string {
    const control = this.form.get(fieldName);
    if (!control?.errors) {
      return '';
    }

    if (control.errors['required']) {
      return 'Campo obrigatório.';
    }
    if (control.errors['email']) {
      return 'Informe um e-mail válido.';
    }
    if (control.errors['minlength'] || control.errors['maxlength']) {
      return 'Confira o tamanho do campo.';
    }

    return 'Valor inválido.';
  }

  private clearPaymentForm(): void {
    this.form.reset({
      usarCartaoCadastrado: false,
      nacionalidade: 'brasileiro',
      localEndereco: 'brasil',
      pais: PAIS_PADRAO,
      cadastrarRecorrencia: false,
    });
  }

  private onlyDigits(value: any): string {
    return String(value ?? '').replace(/\D/g, '');
  }

  private focusFirstInvalidField(): void {
    window.setTimeout(() => {
      const firstInvalid = document.querySelector<HTMLElement>('.credit-card-modal .ng-invalid[formControlName]');
      firstInvalid?.focus();
    });
  }
}
